import {
  ArrowBackIcon,
  CheckIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import { ConnectionModel } from "../../../models/workFlow/ConnectionModel";
import {
  AzureFunctionModel,
  SYSTEM_PARAMETERS,
  SystemParameter,
  TRIGGER_TYPE_OPTIONS,
  UIActionFlowConfig,
  UIActionParameterMapping,
} from "../../../models/workFlow/UIActionModel";
import { fetchConnectionById } from "../../../services/AdminServices/ConnectionService";
import {
  fetchAzureFunctions,
  fetchFunctionParameters,
  fetchSwaggerRequestResponse,
  loadUIActionFlow,
  saveUIActionFlow,
} from "../../../services/AdminServices/UIActionService";

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  mail: "blue",
  user: "green",
  tenant: "purple",
};

const PARAM_TYPE_COLORS: Record<string, string> = {
  string: "gray",
  number: "orange",
  datetime: "cyan",
  boolean: "teal",
  array: "pink",
};

// ─── Component ───────────────────────────────────────────────────────────────

const UIActionFlowBuilder: React.FC = () => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, userData } = useAppContext();
  const toast = useToast();

  const connId = Number(connectionId);

  // ── State ────────────────────────────────────────────────────────────────
  const [connection, setConnection] = useState<ConnectionModel | null>(
    (location.state as any)?.connection ?? null
  );
  const [azureFunctions, setAzureFunctions] = useState<AzureFunctionModel[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<AzureFunctionModel | null>(null);
  const [mappings, setMappings] = useState<UIActionParameterMapping[]>([]);
  const [flowName, setFlowName] = useState("UI Action Flow");
  const [existingFlowId, setExistingFlowId] = useState<number | undefined>(undefined);

  const [functionSearch, setFunctionSearch] = useState("");
  const [systemParamSearch, setSystemParamSearch] = useState("");
  const [systemParamCategory, setSystemParamCategory] = useState<string>("all");

  const [loadingConnection, setLoadingConnection] = useState(!connection);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const [loadingParams, setLoadingParams] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Load connection if not passed via router state ────────────────────────
  useEffect(() => {
    if (connection || !accessToken || !connId) return;

    const load = async () => {
      try {
        const results = await fetchConnectionById(accessToken, connId);
        if (results.length > 0) setConnection(results[0]);
      } catch {
        toast({ title: "Error", description: "Failed to load connection details.", status: "error", duration: 4000, isClosable: true });
      } finally {
        setLoadingConnection(false);
      }
    };
    load();
  }, [accessToken, connId, connection, toast]);

  // ── Load Azure functions for this connection ──────────────────────────────
  useEffect(() => {
    if (!accessToken || !connId) return;

    const load = async () => {
      setLoadingFunctions(true);
      try {
        const fns = await fetchAzureFunctions(accessToken, connId);
        setAzureFunctions(fns);
      } catch {
        toast({ title: "Warning", description: "Could not load Azure Functions from backend. You may need to configure them first.", status: "warning", duration: 5000, isClosable: true });
      } finally {
        setLoadingFunctions(false);
      }
    };
    load();
  }, [accessToken, connId, toast]);

  // ── Load existing saved flow ──────────────────────────────────────────────
  useEffect(() => {
    if (!accessToken || !connId) return;

    const load = async () => {
      try {
        const saved = await loadUIActionFlow(accessToken, connId);
        if (!saved || !saved.config) return;

        setExistingFlowId(saved.flowId);
        if (saved.flowName) setFlowName(saved.flowName);

        // Restore previously selected function + mappings
        const { config } = saved;
        setMappings(config.parameterMappings ?? []);

        // If we have an existing function selected, find it after functions load
        if (config.selectedFunctionId) {
          setSelectedFunction((prev) => prev ?? { functionId: config.selectedFunctionId! } as AzureFunctionModel);
        }
      } catch {
        // No existing flow — fine
      }
    };
    load();
  }, [accessToken, connId]);

  // ── Restore full function object once functions are loaded ────────────────
  useEffect(() => {
    if (azureFunctions.length === 0 || !selectedFunction) return;
    if (selectedFunction.functionName) return; // already fully loaded

    const match = azureFunctions.find((f) => f.functionId === selectedFunction.functionId);
    if (match) handleSelectFunction(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureFunctions]);

  const applyMappingsForInputParameters = useCallback((inputParams: any[]) => {
    setMappings((prev) => {
      const existingMap = new Map(prev.map((m) => [m.functionParam, m]));
      return inputParams.map((p, i) => {
        if (existingMap.has(p.paramName)) return existingMap.get(p.paramName)!;
        return {
          id: i + 1,
          functionParam: p.paramName,
          paramType: p.paramType,
          isRequired: p.isRequired,
          sourceType: "system",
          systemParamKey: "",
          manualValue: "",
        } as UIActionParameterMapping;
      });
    });
  }, []);

  const loadSwaggerRequestResponseForFunction = useCallback(
    async (fn: AzureFunctionModel) => {
      if (!accessToken || !connId) return;
      const swaggerUrl = (connection?.swaggerUrl ?? "").trim();
      if (!connection?.isSwaggerEnabled || !swaggerUrl) return;

      try {
        const params = await fetchSwaggerRequestResponse(accessToken, {
          connectionId: connId,
          requestType: fn.functionName,
          requestGroup: fn.requestGroup ?? "get",
          swaggerUrl,
        });

        const inputParams = params.inputParameters ?? [];
        const outputParams = params.outputParameters ?? [];
        setSelectedFunction((prev) =>
          prev && prev.functionId === fn.functionId
            ? { ...prev, inputParameters: inputParams, outputParameters: outputParams }
            : prev
        );
        applyMappingsForInputParameters(inputParams);
      } catch {
        toast({
          title: "Error",
          description: "Failed to fetch request/response from backend.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [accessToken, applyMappingsForInputParameters, connId, connection?.isSwaggerEnabled, connection?.swaggerUrl, toast]
  );

  // ── Select a function: load its parameters and initialise mappings ────────
  const handleSelectFunction = useCallback(
    async (fn: AzureFunctionModel) => {
      if (!accessToken) return;
      setSelectedFunction(fn);
      setLoadingParams(true);

      try {
        let inputParams = fn.inputParameters ?? [];
        let outputParams = fn.outputParameters ?? [];

        // If not already populated, fetch from backend
        if (inputParams.length === 0 && outputParams.length === 0 && fn.functionId > 0) {
          const params = await fetchFunctionParameters(accessToken, fn.functionId);
          inputParams = params.inputParameters;
          outputParams = params.outputParameters;
          setSelectedFunction({ ...fn, inputParameters: inputParams, outputParameters: outputParams });
        }

        applyMappingsForInputParameters(inputParams);

        if (connection?.isSwaggerEnabled && (connection.swaggerUrl ?? "").trim()) {
          await loadSwaggerRequestResponseForFunction(fn);
        }
      } catch {
        toast({ title: "Error", description: "Failed to load function parameters.", status: "error", duration: 4000, isClosable: true });
      } finally {
        setLoadingParams(false);
      }
    },
    [accessToken, applyMappingsForInputParameters, connection?.isSwaggerEnabled, connection?.swaggerUrl, loadSwaggerRequestResponseForFunction, toast]
  );

  // ── Update a single mapping row ───────────────────────────────────────────
  const updateMapping = (id: number, changes: Partial<UIActionParameterMapping>) => {
    setMappings((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...changes } : m))
    );
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!connection || !selectedFunction) {
      toast({ title: "Incomplete", description: "Please select an Azure Function before saving.", status: "warning", duration: 3000, isClosable: true });
      return;
    }

    const requiredUnmapped = mappings.filter(
      (m) => m.isRequired && m.sourceType === "system" && !m.systemParamKey
    );
    if (requiredUnmapped.length > 0) {
      toast({
        title: "Required parameters unmapped",
        description: `Please map the following required parameters: ${requiredUnmapped.map((m) => m.functionParam).join(", ")}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }

    const config: UIActionFlowConfig = {
      connectionId: connId,
      triggerType: connection.triggerType ?? "",
      selectedFunctionId: selectedFunction.functionId,
      functionName: selectedFunction.functionName,
      functionUrl: selectedFunction.functionUrl,
      parameterMappings: mappings,
    };

    setSaving(true);
    try {
      const result = await saveUIActionFlow(
        accessToken,
        config,
        existingFlowId,
        flowName,
        userData?.userID
      );

      if (result.success) {
        if (result.executionFlowId) setExistingFlowId(result.executionFlowId);
        toast({ title: "Saved", description: result.message, status: "success", duration: 3000, isClosable: true });
      } else {
        toast({ title: "Error", description: result.message, status: "error", duration: 5000, isClosable: true });
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Derived / memoised ────────────────────────────────────────────────────
  const filteredFunctions = useMemo(() => {
    const q = functionSearch.toLowerCase();
    return azureFunctions.filter((f) => {
      const functionName = (f.functionName ?? "").toLowerCase();
      const description = (f.description ?? "").toLowerCase();
      return functionName.includes(q) || description.includes(q);
    });
  }, [azureFunctions, functionSearch]);

  const filteredSystemParams = useMemo(() => {
    let params: SystemParameter[] = SYSTEM_PARAMETERS;
    if (systemParamCategory !== "all") params = params.filter((p) => p.category === systemParamCategory);
    if (systemParamSearch) {
      const q = systemParamSearch.toLowerCase();
      params = params.filter(
        (p) => p.label.toLowerCase().includes(q) || p.key.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
      );
    }
    return params;
  }, [systemParamCategory, systemParamSearch]);

  const triggerOption = TRIGGER_TYPE_OPTIONS.find(
    (t) => t.value === connection?.triggerType
  );

  // ── Loading screen ────────────────────────────────────────────────────────
  if (loadingConnection) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={3}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.500">Loading connection...</Text>
        </VStack>
      </Flex>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box p={5} bg="gray.50" minH="100vh">
      {/* ── Header ── */}
      <Flex
        justify="space-between"
        align="center"
        mb={5}
        bg="white"
        p={4}
        borderRadius="lg"
        boxShadow="sm"
        wrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <IconButton
            aria-label="Back to connections"
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate("/connection")}
          />
          <VStack align="start" spacing={0}>
            <Heading size="md" color="gray.800">
              UI Action Flow Builder
            </Heading>
            {connection && (
              <HStack spacing={2} mt={1}>
                <Text fontSize="sm" color="gray.500">
                  {connection.name}
                </Text>
                {triggerOption && (
                  <Badge colorScheme="blue" variant="subtle">
                    {triggerOption.label}
                  </Badge>
                )}
                {existingFlowId && (
                  <Badge colorScheme="green" variant="subtle">
                    <HStack spacing={1}>
                      <CheckIcon boxSize={2} />
                      <Text>Saved</Text>
                    </HStack>
                  </Badge>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>

        <HStack spacing={3}>
          <FormControl w="220px">
            <Input
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              placeholder="Flow name"
              size="sm"
              bg="gray.50"
            />
          </FormControl>
          <Button
            colorScheme="blue"
            leftIcon={<CheckIcon />}
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
            size="sm"
          >
            Save Flow
          </Button>
        </HStack>
      </Flex>

      {/* ── Trigger type info banner ── */}
      {triggerOption && (
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="md"
          px={4}
          py={3}
          mb={5}
        >
          <HStack spacing={3}>
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
              Trigger: {triggerOption.label}
            </Badge>
            <Text fontSize="sm" color="blue.700">
              {triggerOption.description}
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Three-column grid ── */}
      <Grid
        templateColumns={{ base: "1fr", lg: "260px 1fr 280px" }}
        gap={4}
        alignItems="start"
      >
        {/* ── Column 1: Azure Functions list ── */}
        <GridItem>
          <Card boxShadow="sm" h="full">
            <CardHeader pb={2}>
              <Heading size="sm" color="gray.700">
                Azure Functions
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <InputGroup size="sm">
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search functions..."
                    value={functionSearch}
                    onChange={(e) => setFunctionSearch(e.target.value)}
                  />
                </InputGroup>

                {loadingFunctions ? (
                  <Flex justify="center" py={6}>
                    <Spinner size="md" color="blue.400" />
                  </Flex>
                ) : filteredFunctions.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <Text fontSize="sm" color="gray.400">
                      {azureFunctions.length === 0
                        ? "No Azure Functions found for this connection."
                        : "No functions match your search."}
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={2} align="stretch" maxH="500px" overflowY="auto">
                    {filteredFunctions.map((fn) => {
                      const isSelected = selectedFunction?.functionId === fn.functionId;
                      return (
                        <Box
                          key={fn.functionId}
                          p={3}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={isSelected ? "blue.400" : "gray.200"}
                          bg={isSelected ? "blue.50" : "white"}
                          cursor="pointer"
                          _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                          onClick={() => handleSelectFunction(fn)}
                          transition="all 0.15s"
                        >
                          <HStack justify="space-between" mb={1}>
                            <Text
                              fontWeight={isSelected ? "700" : "600"}
                              fontSize="sm"
                              color={isSelected ? "blue.700" : "gray.700"}
                              noOfLines={1}
                            >
                              {fn.functionName}
                            </Text>
                            {isSelected && (
                              <Badge colorScheme="blue" size="sm">Selected</Badge>
                            )}
                          </HStack>
                          {fn.description && (
                            <Text fontSize="xs" color="gray.500" noOfLines={2}>
                              {fn.description}
                            </Text>
                          )}
                          <HStack mt={1} spacing={1} flexWrap="wrap">
                            <Tag size="sm" colorScheme="orange" variant="subtle">
                              <TagLabel>
                                {(fn.inputParameters?.length ?? 0)} inputs
                              </TagLabel>
                            </Tag>
                            <Tag size="sm" colorScheme="teal" variant="subtle">
                              <TagLabel>
                                {(fn.outputParameters?.length ?? 0)} outputs
                              </TagLabel>
                            </Tag>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* ── Column 2: Parameter mapping table ── */}
        <GridItem>
          <VStack spacing={4} align="stretch">
            {/* Selected function info */}
            {selectedFunction?.functionName && (
              <Card boxShadow="sm">
                <CardBody py={3}>
                  <HStack justify="space-between" wrap="wrap" gap={2}>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="700" color="gray.800">
                        {selectedFunction.functionName}
                      </Text>
                      {selectedFunction.functionUrl && (
                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          {selectedFunction.functionUrl}
                        </Text>
                      )}
                    </VStack>
                    <HStack spacing={2}>
                      <Badge colorScheme="orange" variant="subtle">
                        {selectedFunction.inputParameters?.length ?? 0} input params
                      </Badge>
                      <Badge colorScheme="teal" variant="subtle">
                        {selectedFunction.outputParameters?.length ?? 0} output params
                      </Badge>
                    </HStack>
                  </HStack>
                </CardBody>
              </Card>
            )}

            {/* Mapping table */}
            <Card boxShadow="sm">
              <CardHeader pb={2}>
                <Heading size="sm" color="gray.700">
                  Parameter Mapping
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Map Azure Function input parameters to system variables or manual values
                </Text>
              </CardHeader>
              <CardBody pt={0}>
                {!selectedFunction?.functionName ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py={12}
                    color="gray.400"
                  >
                    <Text fontSize="lg" mb={2}>
                      ⚡
                    </Text>
                    <Text fontWeight="600" mb={1}>
                      Select an Azure Function
                    </Text>
                    <Text fontSize="sm" textAlign="center">
                      Choose a function from the left panel to configure parameter mappings
                    </Text>
                  </Flex>
                ) : loadingParams ? (
                  <Flex justify="center" py={8}>
                    <VStack spacing={3}>
                      <Spinner size="lg" color="blue.400" />
                      <Text fontSize="sm" color="gray.500">
                        Loading function parameters...
                      </Text>
                    </VStack>
                  </Flex>
                ) : mappings.length === 0 ? (
                  <Box textAlign="center" py={8} color="gray.500">
                    <Text>This function has no input parameters.</Text>
                  </Box>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {/* Column headers */}
                    <Grid templateColumns="1fr 160px 1fr" gap={3} px={2}>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                        Function Parameter
                      </Text>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                        Source Type
                      </Text>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                        Value / System Param
                      </Text>
                    </Grid>
                    <Divider />

                    {mappings.map((mapping) => (
                      <Box
                        key={mapping.id}
                        bg={mapping.isRequired && !mapping.systemParamKey && !mapping.manualValue ? "red.50" : "gray.50"}
                        borderRadius="md"
                        p={3}
                        border="1px solid"
                        borderColor={mapping.isRequired && !mapping.systemParamKey && !mapping.manualValue ? "red.200" : "gray.200"}
                      >
                        <Grid templateColumns="1fr 160px 1fr" gap={3} alignItems="center">
                          {/* Function param name */}
                          <VStack align="start" spacing={1}>
                            <HStack spacing={1} flexWrap="wrap">
                              <Text fontWeight="600" fontSize="sm" color="gray.800" fontFamily="mono">
                                {mapping.functionParam}
                              </Text>
                              {mapping.isRequired && (
                                <Badge colorScheme="red" variant="solid" fontSize="9px">
                                  required
                                </Badge>
                              )}
                            </HStack>
                            <Badge
                              colorScheme={PARAM_TYPE_COLORS[mapping.paramType] ?? "gray"}
                              variant="subtle"
                              fontSize="10px"
                            >
                              {mapping.paramType}
                            </Badge>
                          </VStack>

                          {/* Source type selector */}
                          <Select
                            size="sm"
                            value={mapping.sourceType}
                            onChange={(e) =>
                              updateMapping(mapping.id, {
                                sourceType: e.target.value as UIActionParameterMapping["sourceType"],
                                systemParamKey: "",
                                manualValue: "",
                              })
                            }
                          >
                            <option value="system">System Variable</option>
                            <option value="manual">Manual Value</option>
                            <option value="output">Previous Output</option>
                          </Select>

                          {/* Value input */}
                          {mapping.sourceType === "system" && (
                            <Select
                              size="sm"
                              value={mapping.systemParamKey ?? ""}
                              onChange={(e) =>
                                updateMapping(mapping.id, { systemParamKey: e.target.value })
                              }
                              placeholder="Select system parameter..."
                            >
                              {SYSTEM_PARAMETERS.map((sp) => (
                                <option key={sp.key} value={sp.key}>
                                  {sp.label} ({sp.type})
                                </option>
                              ))}
                            </Select>
                          )}

                          {mapping.sourceType === "manual" && (
                            <Input
                              size="sm"
                              value={mapping.manualValue ?? ""}
                              onChange={(e) =>
                                updateMapping(mapping.id, { manualValue: e.target.value })
                              }
                              placeholder="Enter hardcoded value..."
                            />
                          )}

                          {mapping.sourceType === "output" && (
                            <Input
                              size="sm"
                              value={mapping.manualValue ?? ""}
                              onChange={(e) =>
                                updateMapping(mapping.id, { manualValue: e.target.value })
                              }
                              placeholder="e.g. $.result.id"
                              fontFamily="mono"
                            />
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </VStack>
                )}
              </CardBody>
            </Card>

            {/* Output parameters section */}
            {selectedFunction?.functionName && (selectedFunction.outputParameters?.length ?? 0) > 0 && (
              <Card boxShadow="sm">
                <CardHeader pb={2}>
                  <Heading size="sm" color="gray.700">
                    Output Parameters
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Data returned by the Azure Function after execution
                  </Text>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={2} align="stretch">
                    {selectedFunction.outputParameters.map((param) => (
                      <HStack
                        key={param.paramName}
                        justify="space-between"
                        p={2}
                        bg="teal.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="teal.200"
                      >
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="600" fontFamily="mono" color="teal.800">
                            {param.paramName}
                          </Text>
                          {param.description && (
                            <Tooltip label={param.description} placement="top">
                              <Text fontSize="xs" color="teal.600" cursor="help">
                                ⓘ
                              </Text>
                            </Tooltip>
                          )}
                        </HStack>
                        <Badge
                          colorScheme={PARAM_TYPE_COLORS[param.paramType] ?? "gray"}
                          variant="subtle"
                          fontSize="10px"
                        >
                          {param.paramType}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </GridItem>

        {/* ── Column 3: System parameters reference panel ── */}
        <GridItem>
          <Card boxShadow="sm" position="sticky" top="20px">
            <CardHeader pb={2}>
              <Heading size="sm" color="gray.700">
                System Parameters
              </Heading>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Available mail &amp; context variables for mapping
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <Select
                  size="sm"
                  value={systemParamCategory}
                  onChange={(e) => setSystemParamCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="mail">📧 Mail</option>
                  <option value="user">👤 User</option>
                  <option value="tenant">🏢 Tenant</option>
                </Select>

                <InputGroup size="sm">
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search parameters..."
                    value={systemParamSearch}
                    onChange={(e) => setSystemParamSearch(e.target.value)}
                  />
                </InputGroup>

                <VStack
                  spacing={2}
                  align="stretch"
                  maxH="520px"
                  overflowY="auto"
                  pr={1}
                >
                  {filteredSystemParams.map((param) => (
                    <Box
                      key={param.key}
                      p={2}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      bg="white"
                      _hover={{ bg: "gray.50" }}
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" fontWeight="700" fontFamily="mono" color="gray.700">
                          {param.key}
                        </Text>
                        <Badge
                          colorScheme={CATEGORY_COLORS[param.category]}
                          variant="subtle"
                          fontSize="9px"
                        >
                          {param.category}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        {param.label}
                      </Text>
                      <HStack spacing={1}>
                        <Badge
                          colorScheme={PARAM_TYPE_COLORS[param.type] ?? "gray"}
                          variant="outline"
                          fontSize="9px"
                        >
                          {param.type}
                        </Badge>
                      </HStack>
                      <Text fontSize="10px" color="gray.400" mt={1} noOfLines={2}>
                        {param.description}
                      </Text>
                    </Box>
                  ))}

                  {filteredSystemParams.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Text fontSize="sm" color="gray.400">
                        No parameters match your filter.
                      </Text>
                    </Box>
                  )}
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default UIActionFlowBuilder;
